import React, { useRef, useState } from 'react';
import { ZoomIn, ZoomOut } from 'lucide-react';

const LENS = 180;
const SCALE = 2;

// Large swatch image with a hover lens that shows the texture at 2× (toggle on/off)
export const MagnifierImage = ({ src, alt, className = '' }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(true);
  const [lens, setLens] = useState(null);

  const onMove = (e) => {
    if (!active || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < 0 || y < 0 || x > r.width || y > r.height) { setLens(null); return; }
    setLens({ x, y, w: r.width, h: r.height });
  };

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${active ? 'cursor-crosshair' : 'cursor-default'} ${className}`}
      onMouseMove={onMove}
      onMouseLeave={() => setLens(null)}
      data-testid="magnifier-image"
    >
      <img src={src} alt={alt} className="w-full h-full object-cover select-none" draggable={false} />
      {active && lens && (
        <div
          className="pointer-events-none absolute rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.25),0_12px_30px_rgba(0,0,0,0.35)]"
          style={{
            width: LENS,
            height: LENS,
            left: lens.x - LENS / 2,
            top: lens.y - LENS / 2,
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${lens.w * SCALE}px ${lens.h * SCALE}px`,
            backgroundPosition: `${-(lens.x * SCALE - LENS / 2)}px ${-(lens.y * SCALE - LENS / 2)}px`,
          }}
          data-testid="magnifier-lens"
        >
          <span className="absolute bottom-2 right-3 text-[10px] font-semibold text-white drop-shadow">2×</span>
        </div>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setActive((a) => !a); setLens(null); }}
        className={`absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-md transition-colors ${active ? 'bg-neutral-900 text-white hover:bg-neutral-800' : 'bg-white/90 text-neutral-700 hover:bg-white'}`}
        title={active ? 'Turn off 2× magnifier' : 'Turn on 2× magnifier'}
        data-testid="magnifier-toggle"
      >
        {active ? <ZoomOut className="w-3.5 h-3.5" /> : <ZoomIn className="w-3.5 h-3.5" />}
        {active ? 'Magnifier 2× on' : 'Magnifier 2×'}
      </button>
      {active && !lens && (
        <span className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-black/50 px-2 py-1 text-[11px] text-white backdrop-blur-sm">
          Hover the fabric to magnify the weave
        </span>
      )}
    </div>
  );
};
